"use client";

/**
 * The front door's clay photo in its porcelain frame, with Bash speaking from it: his bubble sits
 * just above his head and the black "Your seat" tag points at the empty chair. Both follow the
 * photo as the frame crops it (cover.ts), on every resize. Ported from vestra-v3 (the sign-in's
 * .su-pic and the waitlist's .pic).
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { cn } from "@/lib/utils";
import { bubbleAt, coverPoint } from "./cover";

export interface ClayPictureProps {
  src: string;
  srcSet: string;
  sizes: string;
  alt: string;
  /** Bash's head in the photo, fractions of its natural size. */
  head: [number, number];
  /** The empty chair's top, fractions of its natural size. */
  seat: [number, number];
  /** What Bash says (the words, under his name). */
  say: ReactNode;
  /** Bash is typing: the dots show instead of the words. */
  typing: boolean;
  /** The frame's own layout (size, margins, rounding). */
  className?: string;
}

const POSITION: [number, number] = [0.5, 0.3];

export function ClayPicture({
  src,
  srcSet,
  sizes,
  alt,
  head,
  seat,
  say,
  typing,
  className,
}: ClayPictureProps) {
  const frame = useRef<HTMLElement>(null);
  const img = useRef<HTMLImageElement>(null);
  const bubble = useRef<HTMLParagraphElement>(null);
  const [spots, setSpots] = useState<{
    say: [number, number];
    seat: [number, number];
  } | null>(null);

  const place = useCallback(() => {
    const el = img.current;
    const b = bubble.current;
    if (!el || !b || !el.clientWidth) return;
    const box = {
      inset: el.offsetLeft,
      width: el.clientWidth,
      height: el.clientHeight,
      naturalWidth: el.naturalWidth || 1600,
      naturalHeight: el.naturalHeight || 1000,
      position: POSITION,
    };
    const [cx, cy] = coverPoint(box, seat);
    setSpots({
      say: bubbleAt(box, head, {
        width: b.offsetWidth,
        height: b.offsetHeight,
      }),
      seat: [Math.round(cx), Math.round(cy)],
    });
  }, [head, seat]);

  useEffect(() => {
    const el = frame.current;
    if (!el) return;
    place();
    const observer = new ResizeObserver(place);
    observer.observe(el);
    return () => observer.disconnect();
  }, [place]);

  // The words change the bubble's size, so it is placed again whenever they do.
  useEffect(() => {
    place();
  }, [typing, say, place]);

  return (
    <figure
      ref={frame}
      className={cn(
        "relative rounded-[32px] bg-fd-porcelain p-2.5 shadow-fd-frame",
        className
      )}
    >
      <img
        ref={img}
        src={src}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        fetchPriority="high"
        decoding="async"
        onLoad={place}
        className="absolute inset-2.5 h-[calc(100%-20px)] w-[calc(100%-20px)] rounded-[22px] bg-fd-photo object-cover object-[50%_30%] motion-safe:animate-fd-fade max-fold:inset-2 max-fold:h-[calc(100%-16px)] max-fold:w-[calc(100%-16px)] max-fold:rounded-[19px]"
      />
      <p
        ref={bubble}
        aria-live="polite"
        className={cn(
          "absolute z-[2] m-0 w-max max-w-[min(270px,calc(100%-40px))] -translate-y-full rounded-[18px_18px_18px_6px] bg-white px-4 pt-2.5 pb-3 text-base leading-[1.38] font-medium text-balance text-fd-ink shadow-fd-say origin-bottom-left motion-safe:animate-fd-pop motion-safe:[animation-delay:0.6s] max-fold:px-3 max-fold:pt-2 max-fold:pb-2.5 max-fold:text-sm",
          !spots && "invisible"
        )}
        style={spots ? { left: spots.say[0], top: spots.say[1] } : undefined}
      >
        <b className="mb-0.5 block text-xs font-bold text-fd-bash-ink">Bash</b>
        {typing ? (
          <span
            className="flex gap-1 px-0.5 py-1.5"
            aria-label="Bash is typing"
          >
            {[0, 1, 2].map((i) => (
              <i
                key={i}
                className="size-1.5 rounded-full bg-fd-ink-4 motion-safe:animate-fd-dot [&:nth-child(2)]:[animation-delay:0.15s] [&:nth-child(3)]:[animation-delay:0.3s]"
              />
            ))}
          </span>
        ) : (
          <span>{say}</span>
        )}
      </p>
      <p
        aria-hidden="true"
        className={cn(
          "absolute z-[2] m-0 inline-flex h-7.5 -translate-x-1/2 -translate-y-full items-center gap-2 rounded-full bg-fd-ink pr-3 pl-2 text-xs font-bold whitespace-nowrap text-white shadow-fd-seat motion-safe:animate-fd-pop motion-safe:[animation-delay:1.2s] after:absolute after:-bottom-1 after:left-1/2 after:-ml-1 after:size-2 after:rotate-45 after:rounded-[2px] after:bg-fd-ink max-fold:h-6.5 max-fold:pr-3 max-fold:pl-2 max-fold:text-xs",
          !spots && "invisible"
        )}
        style={
          spots ? { left: spots.seat[0], top: spots.seat[1] - 10 } : undefined
        }
      >
        <i className="relative size-2 rounded-full bg-fd-live after:absolute after:inset-0 after:rounded-full after:bg-fd-live motion-safe:after:animate-fd-live" />
        Your seat
      </p>
    </figure>
  );
}
